import React, { useState, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
} from '@mui/material';
import BrandMark from '../components/BrandMark';
import { useAuth } from '../hooks/useAuth';

const AdminLogin = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Admin login form submitted:', { email: form.email, loading });
    
    if (loading) {
      console.log('Login already in progress, ignoring submit');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      console.log('Calling login function with isAdmin=true');
      await login(form.email, form.password, true);
      console.log('Login function completed');
      
      // Add additional debugging
      setTimeout(() => {
        console.log('Checking auth state after login...');
        console.log('Current URL:', window.location.href);
        console.log('Current pathname:', window.location.pathname);
        
        // Check if we're still on login page
        if (window.location.pathname === '/admin/login') {
          console.warn('⚠️ Still on login page after successful login - possible navigation issue');
          console.log('Attempting manual navigation...');
          window.location.href = '/admin/dashboard';
        }
      }, 500);
      
    } catch (err: any) {
      console.error('Login error:', err);
      const msg = err.response?.data?.error || 'Login failed';
      setError(msg);
    } finally {
      console.log('Login attempt finished, setting loading to false');
      setLoading(false);
    }
  }, [form.email, form.password, login, loading]);

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <BrandMark size={36} />
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.02em' }}>
              Workflow.Estate
            </Typography>
          </Box>
          <Typography component="h1" variant="h5">
            Admin Login
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={form.email}
              onChange={handleChange}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={form.password}
              onChange={handleChange}
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AdminLogin; 